import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
// types
import { CroutonIds, CroutonPriority, SwiggySixesCroutonComms } from "@swiggy-private/cart-types";
// context
import { useEffectOnce, CartIPLSwiggySixesContext } from "@swiggy-private/core-ui";
import { AnalyticsContext, CartOverlaysContext } from "@swiggy-private/checkout-core";
// utils
import { format, isEmpty } from "@swiggy-private/js-utils";
// helpers
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";
// analytics
import {
    getSSFloatyContext,
    fireImpressionOnce,
    resetImpressionOnce,
    clickSSFloatyCloseEvent,
} from "@cart-client/analytics/events/IPLSwiggySixesFloaty";
// styles
import { Container } from "./styles";
import styles from "./IPLSwiggySixesTimerCrouton.scss";
// constants
import { TICKER_COMMS_INTERVAL, SWIGGY_SIXES_CROUTON_DATA_TEST_IDS as IDS } from "./constants";

export interface IPLSwiggySixesTimerCroutonProps {
    croutonData: SwiggySixesCroutonComms;
    setCroutonVisibility: (visibility: boolean) => void;
    /**
     * When true, this instance does not self-register.
     * - MRO: list-driven registration owns register; this instance still runs ticker/hide side-effects.
     * - Non-MRO: `useIPLSwiggySixesCroutonRegistration` also mounts with this flag so it stays
     *   payload-only; CartRenderer's call (flag false) remains the sole BAU driver.
     */
    skipSelfRegister?: boolean;
}

export const useIPLSwiggySixesTimerCrouton = ({
    croutonData,
    setCroutonVisibility,
    skipSelfRegister = false,
}: IPLSwiggySixesTimerCroutonProps) => {
    const { tickerTimerInMinSec, timestampInMs, isCroutonEnabledInSession, setCroutonTrackingInSession } =
        useContext(CartIPLSwiggySixesContext);
    const { registerCrouton, onCloseCrouton } = useContext(CartOverlaysContext);
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const impressionEventRef = useRef(impressionEvent);
    const clickEventRef = useRef(clickEvent);
    const hasFiredImpressionRef = useRef(false);
    impressionEventRef.current = impressionEvent;
    clickEventRef.current = clickEvent;
    const { croutonComms, icon, croutonState } = croutonData;
    const iterationCountRef = useRef(0);
    const [commTitleIdx, setCommTitleIdx] = useState(0);
    const tickerCommsTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const containerRef = useRef<HTMLDivElement>(null);
    const isMroFlow = isMROFlow();
    const { enabledCroutonId } = useContext(CartOverlaysContext);
    const isIplActiveCrouton = enabledCroutonId === CroutonIds.IPL_SWIGGY_SIXES;

    // Payload-only copy mounted from useIPLSwiggySixesCroutonRegistration in BAU (non-MRO).
    // CartRenderer's instance owns ticker/hide/close there, so this copy must stay inert.
    const isNonMROPayloadOnlyInstance = skipSelfRegister && !isMroFlow;

    const tickerValue = useMemo(() => {
        const { minutes = 0, seconds = 0 } = tickerTimerInMinSec;
        return { minutes, seconds };
    }, [tickerTimerInMinSec]);

    const cleanupTickerComms = useCallback(() => {
        if (!isEmpty(tickerCommsTimerRef.current)) {
            clearInterval(tickerCommsTimerRef.current);
        }
    }, []);

    const startTickerComms = useCallback(() => {
        if (!isEmpty(croutonComms)) {
            cleanupTickerComms();
            iterationCountRef.current = 0;
            setCommTitleIdx(0);
            tickerCommsTimerRef.current = setInterval(() => {
                setCommTitleIdx((idx) => {
                    const nextIdx = idx + 1;

                    // If we haven't reached the end of current array
                    if (nextIdx < croutonComms.length) {
                        return nextIdx;
                    }

                    // If we've reached the end, check iteration count
                    if (iterationCountRef.current === 0) {
                        // First iteration complete, start second
                        iterationCountRef.current = 1;
                        return 0;
                    }
                    // Second iteration complete, stay at last message
                    cleanupTickerComms();
                    return croutonComms.length - 1;
                });
            }, TICKER_COMMS_INTERVAL);
        }
    }, [cleanupTickerComms, croutonComms]);

    useEffect(() => {
        if (containerRef.current) {
            const element = containerRef.current;
            element.classList.remove(styles.fadeOut);

            const fadeTimeout = setTimeout(() => {
                element.classList.add(styles.fadeOut);
            }, 800); // Increased to slow down the transition

            return () => {
                clearTimeout(fadeTimeout);
            };
        }
    }, [commTitleIdx, croutonState]);

    const titleNode = useMemo(() => {
        if (!isEmpty(croutonComms?.[commTitleIdx])) {
            return (
                <Container
                    ref={containerRef}
                    data-testid={IDS.TEXT}
                    key={`${croutonState}-${commTitleIdx}-${Date.now()}`}
                    className={styles.croutonText}
                >
                    {croutonComms?.[commTitleIdx]}
                </Container>
            );
        }
        return null;
    }, [commTitleIdx, croutonComms, croutonState]);

    const payloadForCroutonRegister = useMemo(() => {
        return {
            title: titleNode,
            showTimer: true,
            precalculatedTimerDuration: tickerValue,
            icon: icon,
            buttonClassName: styles.iplSwiggySixesCroutonButton,
            timerClassName: styles.iplSwiggySixesCroutonTimer,
            iconClassName: styles.iplSwiggySixesCroutonIcon,
            customWrapperDataTestId: format(IDS.CONTAINER, croutonState),
            iconHeight: 24 * 3,
            iconWidth: 24 * 3,
            impressionEvent: () =>
                fireImpressionOnce(
                    hasFiredImpressionRef,
                    impressionEventRef.current,
                    getSSFloatyContext(croutonState, tickerValue),
                ),
            onClose: () =>
                clickSSFloatyCloseEvent(clickEventRef.current, getSSFloatyContext(croutonState, tickerValue)),
        };
    }, [croutonState, icon, tickerValue, titleNode]);

    useEffect(() => {
        // BAU payload-only copy: CartRenderer owns hide/close.
        if (isNonMROPayloadOnlyInstance) {
            return;
        }
        if (isMroFlow) {
            if (isIplActiveCrouton && !croutonData?.enabled) onCloseCrouton();
        }
        // if crouton was enabled in component memory before but now crouton data is disabled we need to hide now
        // if crouton was enabled in session before but now crouton data is disabled we need to hide now
        else if (isCroutonEnabledInSession?.() && !croutonData?.enabled) {
            onCloseCrouton();
            setCroutonTrackingInSession?.(false);
        }
        if (!croutonData?.enabled) resetImpressionOnce(hasFiredImpressionRef);
    }, [
        croutonData?.enabled,
        onCloseCrouton,
        isCroutonEnabledInSession,
        setCroutonTrackingInSession,
        isMroFlow,
        isNonMROPayloadOnlyInstance,
        isIplActiveCrouton,
    ]);

    useEffect(() => {
        // External owner (MRO list loop / BAU payload-only copy) toggles skipSelfRegister.
        if (croutonData?.enabled && !isEmpty(payloadForCroutonRegister?.title) && !skipSelfRegister) {
            registerCrouton({
                content: payloadForCroutonRegister,
                priority: CroutonPriority.PRIORITY_ORDER_ONE,
                id: CroutonIds.IPL_SWIGGY_SIXES,
                // as the content of the crouton has to keep updating need to pass this flag
                forceRefreshContent: true,
            });
        }
    }, [payloadForCroutonRegister, registerCrouton, croutonData?.enabled, setCroutonVisibility, skipSelfRegister]);

    useEffect(() => {
        // BAU payload-only copy: CartRenderer owns the comms ticker.
        if (isNonMROPayloadOnlyInstance) {
            return;
        }
        // Start ticker whenever we have new comms, state changes, or timestamp changes
        if (!isEmpty(croutonComms) && !isEmpty(timestampInMs) && !isEmpty(croutonState) && croutonData?.enabled) {
            // mark IPL sixes crouton as visible for other elements to be hidden
            setCroutonVisibility(true);
            setCommTitleIdx(0); // Force reset index before starting
            startTickerComms();
        }
        return () => {
            cleanupTickerComms(); // Clean up on state change
        };
    }, [
        croutonState,
        timestampInMs,
        startTickerComms,
        croutonComms,
        croutonData?.enabled,
        cleanupTickerComms,
        setCroutonVisibility,
        isNonMROPayloadOnlyInstance,
    ]);

    useEffectOnce(() => {
        return () => {
            cleanupTickerComms();
        };
    });

    // Exposed so useGenericCrouton can drive registration via the crouton list in MRO flow.
    return { payloadForCroutonRegister };
};
